/* drl-video — send a product video at a size the tile can actually use.

   The problem it solves. Liquid's `media.sources` exposes exactly two entries:
   the HLS manifest and the single highest-quality mp4. The 480p and 720p mp4s
   Shopify also generates are on the CDN but not in Liquid — confirmed against
   the Admin API on both video products in this store.

   The previous code guessed their filenames out of the 1080p URL:

     replace: 'HD-1080p-2.5Mbps', 'HD-720p-1.6Mbps'

   Shopify bakes the ENCODED bitrate into the filename and it differs per
   upload — 2.5Mbps on daily-synbiotic, 7.2Mbps on ortoped-pro-talpbetet. So
   the replace matched the product it was written against and silently missed
   the next one, emitting three <source> tags that all pointed at the 1080p
   master. 8.2 MB, for a 715px tile, on mobile too. A guessed bitrate is not
   merely suboptimal either: the CDN answers 404 for it.

   The fix. The HLS manifest is a public ~600 byte text file that lists every
   rendition with its real resolution and bitrate, and the mp4 filename is
   derivable from the HLS variant filename. So: read the manifest, pick the
   rendition that fits the tile, play that. One small request buys a 1.7 MB
   file instead of an 8.2 MB one.

   Nothing is fetched until the video is wanted — in view for the gallery hero,
   slide-activated for the lightbox copy. */
(function () {
  // <hash>.HD-720p.4.5Mbps.hls.84306000_.m3u8 -> <hash>.HD-720p-4.5Mbps-84306000.mp4
  var VARIANT = /^(.*)\.([A-Z]{2}-\d+p)\.(.+?)\.hls\.(\d+)_\.m3u8$/;

  // The PDP tile is 715px and the lightbox is viewport-bound, so 1080p is never
  // the right file for anyone. Cap at 720p, lower per element via
  // data-drl-video-max (showcase tiles are 300-500px and take 480). Ignore
  // devicePixelRatio on purpose: a retina phone does not want 8 MB to render a
  // thumbnail crisply.
  function wantedHeight(video) {
    var cap = parseInt(video.dataset.drlVideoMax || '720', 10);
    var width = video.getBoundingClientRect().width || window.innerWidth;
    return Math.min(width > 540 ? 720 : 480, cap);
  }

  // Reduced motion means the poster, and no video bytes at all.
  function motionAllowed() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  var manifests = {};

  // Shared per URL: the gallery and the lightbox render the same media, and
  // one fetch should serve both.
  function readManifest(src) {
    // Liquid hands out protocol-relative URLs (//shop.example/cdn/...). fetch()
    // takes those, but `new URL(file, base)` throws on a base with no scheme,
    // so resolve it once here rather than at every rendition.
    var url = new URL(src, window.location.href).href;
    if (manifests[url]) return manifests[url];
    manifests[url] = fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        var lines = text.split('\n');
        var found = [];
        for (var i = 0; i < lines.length; i++) {
          var size = /RESOLUTION=\d+x(\d+)/.exec(lines[i]);
          if (!size) continue;
          var parts = VARIANT.exec((lines[i + 1] || '').trim());
          if (!parts) continue;
          found.push({
            height: parseInt(size[1], 10),
            url: new URL(parts[1] + '.' + parts[2] + '-' + parts[3] + '-' + parts[4] + '.mp4', url).href,
          });
        }
        if (!found.length) throw new Error('no renditions');
        return found.sort(function (a, b) { return a.height - b.height; });
      });
    return manifests[url];
  }

  // Smallest rendition that still covers the tile; the largest if none does.
  function pick(renditions, height) {
    for (var i = 0; i < renditions.length; i++) {
      if (renditions[i].height >= height) return renditions[i].url;
    }
    return renditions[renditions.length - 1].url;
  }

  // Resolves once the element has a src. Never rejects — an unreadable
  // manifest falls back to the mp4 Liquid gave us, which is heavy but correct.
  function prepare(video) {
    if (video.dataset.drlVideoReady) return Promise.resolve();
    video.dataset.drlVideoReady = '1';
    var fallback = video.dataset.drlVideoFallback;
    var manifest = video.dataset.drlVideoManifest;
    var use = function (url) {
      if (!url) return;
      video.src = url;
      if (video.preload === 'none') video.preload = 'auto';
    };
    if (!manifest) { use(fallback); return Promise.resolve(); }
    return readManifest(manifest)
      .then(function (renditions) { use(pick(renditions, wantedHeight(video))); })
      .catch(function () { use(fallback); });
  }

  function play(video) {
    return prepare(video).then(function () {
      var played = video.play();
      if (played) played.catch(function () {});
    });
  }

  // drl-product-modal.js drives the lightbox copy through these.
  window.drlVideo = { prepare: prepare, play: play };

  // The gallery hero starts when it is on screen and stops when it is not. On
  // mobile the gallery is a horizontal slider, so the slides the visitor never
  // swipes to cost nothing at all.
  var watcher =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) play(entry.target);
              else if (!entry.target.paused) entry.target.pause();
            });
          },
          { rootMargin: '100px' }
        )
      : null;

  function watch(root) {
    if (!motionAllowed()) return;
    (root || document).querySelectorAll('video[data-drl-video-autoplay]').forEach(function (video) {
      if (video.dataset.drlVideoWatched) return;
      video.dataset.drlVideoWatched = '1';
      if (watcher) watcher.observe(video);
      else play(video);
    });
  }

  watch();
  document.addEventListener('shopify:section:load', function (event) { watch(event.target); });
})();
